import { useEffect, useState } from 'react';
import { loadingService } from '../services/loadingService';
import { RubyGemMark } from './RubyGemMark';

/**
 * Global loading indicator.
 *
 * Regular API traffic shows ONLY the thin top progress bar — the page stays
 * visible and interactive while data loads (pages hold their regions with
 * skeletons). The full-screen overlay is reserved for the opt-in blocking
 * channel (`loadingService.startBlocking()`), meant for flows where the user
 * must not interact mid-flight.
 */
function GlobalLoader() {
  const [isLoading, setIsLoading] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const unsubscribe = loadingService.subscribe(setIsLoading);
    const unsubscribeBlocking = loadingService.subscribeBlocking(setIsBlocking);
    return () => {
      unsubscribe();
      unsubscribeBlocking();
    };
  }, []);

  const active = isLoading || isBlocking;

  useEffect(() => {
    if (active) {
      setProgress(0);
      const interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 15;
        });
      }, 200);
      return () => clearInterval(interval);
    } else {
      setProgress(100);
      const timeout = setTimeout(() => setProgress(0), 300);
      return () => clearTimeout(timeout);
    }
  }, [active]);

  if (!active && progress === 0) return null;

  return (
    <>
      {/* Top progress bar — the only indicator for normal loads */}
      <div className="fixed top-0 left-0 right-0 z-[9999] h-1 pointer-events-none">
        <div
          className="h-full bg-gradient-to-r from-admin-primary to-admin-secondary transition-all duration-300 ease-out shadow-[0_0_8px_rgba(220,20,60,0.6)]"
          style={{ width: `${progress}%`, opacity: active || progress < 100 ? 1 : 0 }}
        />
      </div>

      {/* Full screen overlay — blocking channel only */}
      {isBlocking && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center" role="status" aria-live="polite">
          <RubyGemMark size={80} drawing />
          <span className="sr-only">Loading...</span>
        </div>
      )}
    </>
  );
}

export default GlobalLoader;
